import { worldContext } from './context.js';
import { applyOutcome, deliverDuePosts, postMessage } from './outcomes.js';
import { releasePossession } from './possession.js';
import { evaluate } from './predicates.js';
import { drawWithoutReplacement, seedFrom } from './rng.js';
const unchanged = (state) => ({ state, effects: [] });
// ---------------------------------------------------------------------------
// Win and loss
// ---------------------------------------------------------------------------
/**
 * Re-asks the mission's objectives.
 *
 * Called after every resolution and after every scheduled event, which is what
 * makes "the pitch reaches the CEO and the mission ends immediately" true
 * without the phase having to end first. A win is checked before the losses so
 * that an outcome which satisfies both — the mandate approved on the same send
 * that earned a final strike — reads as the win the player earned.
 *
 * An outcome's own `endsLevel` has already run by the time this is called, so a
 * mission that ended with an authored summary keeps it; this is the safety net
 * for objectives no single outcome declares.
 */
export function evaluateObjectives(loaded, state) {
    if (state.end !== null)
        return unchanged(state);
    const objectives = loaded.pack.objectives;
    const context = worldContext(loaded, state);
    if (evaluate(objectives.win, context)) {
        const end = {
            result: 'win',
            reason: 'objective_met',
            summary: null,
            partialCredit: null,
        };
        return { state: { ...state, end }, effects: [{ type: 'LEVEL_ENDED', end }] };
    }
    for (const condition of objectives.lose) {
        if (!evaluate(condition.when, context))
            continue;
        const end = {
            result: 'loss',
            reason: condition.reason,
            summary: condition.summary ?? null,
            partialCredit: null,
        };
        return { state: { ...state, end }, effects: [{ type: 'LEVEL_ENDED', end }] };
    }
    return unchanged(state);
}
// ---------------------------------------------------------------------------
// Scheduled events
// ---------------------------------------------------------------------------
/** The events declared for a day and phase, in package order. */
function eventsFor(pack, day, phase) {
    return pack.schedule.filter((event) => event.day === day && event.phase === phase);
}
/**
 * Fires every scheduled event for the current day and phase, in declared order.
 *
 * Each event's outcomes are applied in their own declared order through the
 * same `applyOutcome` a rule uses, so a scheduled beat can do anything a
 * resolution can: grant particles, post, mutate the proposal, or end the
 * mission. Posts authored `afterTicks: 0` are delivered immediately so the
 * player sees the phase's opening messages before their first action; anything
 * delayed waits for a tick like any other queued post.
 *
 * A guard (`when`) that is false skips the whole event, which is how a package
 * authors "the CEO only rants if nobody has answered him yet".
 */
export function runScheduledEvents(loaded, state) {
    const due = eventsFor(loaded.pack, state.clock.day, state.clock.phase);
    if (due.length === 0)
        return unchanged(state);
    const effects = [];
    let current = state;
    for (const event of due) {
        if (current.end !== null)
            break;
        const guard = event.when;
        if (guard !== undefined && !evaluate(guard, worldContext(loaded, current)))
            continue;
        effects.push({ type: 'SCHEDULED_EVENT_FIRED', eventId: event.id });
        for (const outcomeId of event.outcomes) {
            if (current.end !== null)
                break;
            const outcome = loaded.outcomes[outcomeId];
            if (outcome === undefined)
                continue;
            const applied = applyOutcome(current, outcome, {
                loaded,
                nodeId: 'player',
                channelId: current.activeChannelId ?? '',
                identity: current.identity,
                source: 'scheduled',
                reactToMessageId: null,
            });
            const delivered = deliverDuePosts(applied.state);
            current = delivered.state;
            effects.push(...applied.effects, ...delivered.effects);
        }
        // The mission may be won or lost by a scheduled event — the Exec Sync
        // evaluation is exactly that — so the objectives are re-asked per event
        // rather than once at the end.
        const judged = evaluateObjectives(loaded, current);
        current = judged.state;
        effects.push(...judged.effects);
    }
    return { state: current, effects };
}
// ---------------------------------------------------------------------------
// Environmental noise
// ---------------------------------------------------------------------------
/**
 * Draws this tick's noise into every readable channel that declares a pool.
 *
 * Called at the *close* of a tick — after queued posts have landed, and in a
 * send after the player's own message has been posted. That ordering is the
 * whole of the burial mechanic: noise physically follows the player's message
 * in the channel, and `burialDepth` counts how deep it buried it.
 *
 * Locked channels are skipped. Their messages are not readable, so filling them
 * would only inflate an unread badge the player cannot clear.
 */
export function emitNoise(loaded, state) {
    const pools = loaded.pack.noise.pools;
    if (pools.length === 0)
        return unchanged(state);
    const effects = [];
    let current = state;
    for (const pool of pools) {
        if (current.channels[pool.channel]?.unlocked !== true)
            continue;
        if (pool.cadence <= 0)
            continue;
        // Keyed by pool and tick rather than counted in the state: adding a pool to
        // one channel must not reshuffle what another channel says on day four.
        const seed = seedFrom({
            runSeed: current.seed,
            day: current.clock.day,
            phase: current.clock.phase,
            eventKey: `noise:${pool.id}:${String(current.clock.tick)}`,
        });
        for (const line of drawWithoutReplacement(pool.messages, seed, pool.cadence)) {
            const post = {
                channel: pool.channel,
                authorNodeId: line.authorNodeId,
                text: line.text,
            };
            const posted = postMessage(current, line.reactions === undefined ? post : { ...post, reactions: line.reactions }, 'noise');
            current = posted.state;
            effects.push({ type: 'MESSAGE_POSTED', message: posted.message });
        }
    }
    return { state: current, effects };
}
/**
 * How many noise messages have piled up on top of `messageId` in its channel.
 *
 * Counts every noise message after it, not only an unbroken run: a reply
 * arriving in the middle of the pile does not un-bury the message underneath it.
 */
export function burialDepth(state, messageId) {
    const message = state.messages.find((entry) => entry.id === messageId);
    if (message === undefined)
        return 0;
    return state.messages.filter((entry) => entry.channelId === message.channelId &&
        entry.ordinal > message.ordinal &&
        entry.origin === 'noise').length;
}
/**
 * The outcome id that replaces a node's resolution when the player's message was
 * buried, or `null` when it was not — because the package authors no burial
 * outcome, because the difficulty profile switches burial off, or because the
 * noise simply did not pile deep enough.
 *
 * At Intern this always returns `null`: the noise still arrives for flavour, and
 * costs nothing.
 */
export function buriedOutcomeId(loaded, state, messageId) {
    if (!state.settings.noiseBurial)
        return null;
    const burial = loaded.pack.noise.burial;
    if (burial === undefined)
        return null;
    if (burialDepth(state, messageId) < burial.threshold)
        return null;
    return burial.outcome;
}
// ---------------------------------------------------------------------------
// Autonomous chatter
// ---------------------------------------------------------------------------
/**
 * Lets each node say one authored thing about how it currently feels.
 *
 * A pool is eligible when its `phase` matches (or it declares none) and its
 * `when` holds against the node's own state vars — so a node whose trust has
 * risen past an authored threshold starts drawing from a different pool, and the
 * player reads the shift in the channel rather than in a number. The draw is
 * seeded per node and pool, so which line comes out is stable for a run and
 * independent of what any other node said.
 *
 * Cast order, then pool order, then the drawn line: entirely deterministic.
 */
export function emitChatter(loaded, state) {
    const effects = [];
    let current = state;
    for (const node of loaded.pack.cast) {
        for (const pool of node.chatter ?? []) {
            if (pool.phase !== undefined && pool.phase !== current.clock.phase)
                continue;
            if (current.channels[pool.channel]?.unlocked !== true)
                continue;
            const guard = pool.when;
            if (guard !== undefined && !evaluate(guard, worldContext(loaded, current, node.id)))
                continue;
            const seed = seedFrom({
                runSeed: current.seed,
                day: current.clock.day,
                phase: current.clock.phase,
                eventKey: `chatter:${node.id}:${pool.id}`,
            });
            const [line] = drawWithoutReplacement(pool.pool, seed, 1);
            if (line === undefined)
                continue;
            const posted = postMessage(current, { channel: pool.channel, authorNodeId: node.id, text: line }, 'node');
            current = posted.state;
            effects.push({ type: 'MESSAGE_POSTED', message: posted.message });
        }
    }
    return { state: current, effects };
}
// ---------------------------------------------------------------------------
// Attention decay
// ---------------------------------------------------------------------------
/**
 * The alignment a puppet loses for a day of being ignored, under this profile.
 *
 * Rounded rather than floored so a small `perDay` under Intern's disabled decay
 * cannot become a permanent slow leak the player has no way to read.
 */
export function decayAmount(state, perDay) {
    return Math.round(perDay * state.settings.attentionDecayMultiplier);
}
/**
 * Attention decay: a possessed puppet that has gone a whole in-game day without
 * being spoken as or spoken to loses alignment, and below the authored floor
 * goes back to being its own person.
 *
 * A **scheduled engine event**, not a timer (design D10) — it fires on entering
 * a new day's morning, before the day's authored events, so a mission that
 * schedules something for a puppet's channel finds the possession already
 * resolved either way. `lastActiveDay` is refreshed by wearing the puppet and by
 * addressing it, so "attention" means the thing the fiction means.
 *
 * `day - lastActiveDay >= 2` is the "full day" test: possessing on day 1 and
 * entering day 2 has not yet cost a whole day of neglect, but entering day 3
 * has — day 2 passed with nobody home.
 *
 * Skipped entirely for a puppet whose node declares no `alignment` var: there is
 * nothing to decay, and treating an absent var as zero would revert a puppet the
 * moment it was acquired.
 */
export function applyAttentionDecay(loaded, state) {
    if (!state.settings.attentionDecayEnabled)
        return unchanged(state);
    if (state.clock.day <= 1)
        return unchanged(state);
    const effects = [];
    let current = state;
    for (const puppet of loaded.pack.puppets) {
        const decay = puppet.decay;
        if (decay === undefined)
            continue;
        const held = current.puppets[puppet.nodeId];
        if (held === undefined || !held.possessed)
            continue;
        if (current.clock.day - (held.lastActiveDay ?? current.clock.day) < 2)
            continue;
        const vars = current.nodes[puppet.nodeId]?.vars ?? {};
        const before = vars['alignment'];
        if (before === undefined)
            continue;
        const alignment = Math.max(0, before - decayAmount(current, decay.perDay));
        current = {
            ...current,
            nodes: {
                ...current.nodes,
                [puppet.nodeId]: { vars: { ...vars, alignment } },
            },
        };
        effects.push({
            type: 'NODE_STATE_CHANGED',
            nodeId: puppet.nodeId,
            var: 'alignment',
            value: alignment,
        });
        if (alignment >= decay.revertThreshold) {
            // Surfaced *before* it becomes critical, which is the whole point of a
            // warn threshold: a puppet must never simply be gone one morning.
            const warned = decay.warnThreshold !== undefined && alignment <= decay.warnThreshold;
            effects.push({ type: 'PUPPET_DECAYED', nodeId: puppet.nodeId, alignment, warned });
            continue;
        }
        current = releasePossession(loaded, current, puppet.nodeId, effects);
        effects.push({ type: 'PUPPET_REVERTED', nodeId: puppet.nodeId, alignment });
    }
    return { state: current, effects };
}
// ---------------------------------------------------------------------------
// Entering a phase
// ---------------------------------------------------------------------------
/**
 * Everything the world does when a phase is entered, in the documented order.
 *
 * Shared by `ADVANCE_PHASE` and by `createGame`, because the opening phase is
 * entered too: a mission whose day-1 Overnight Flurry grants the particle the
 * first pitch needs must have fired it before the player is handed the composer.
 */
export function enterPhase(loaded, state) {
    const effects = [];
    let current = state;
    // A new day first settles who the player still is. Everything below it —
    // the day's events, its chatter, its objectives — should read the possessions
    // that survived the night, not the ones that were about to lapse.
    if (current.clock.phase === 'morning') {
        const decayed = applyAttentionDecay(loaded, current);
        current = decayed.state;
        effects.push(...decayed.effects);
    }
    const scheduled = runScheduledEvents(loaded, current);
    current = scheduled.state;
    effects.push(...scheduled.effects);
    if (current.end === null) {
        const chatter = emitChatter(loaded, current);
        current = chatter.state;
        effects.push(...chatter.effects);
        const judged = evaluateObjectives(loaded, current);
        current = judged.state;
        effects.push(...judged.effects);
    }
    return { state: current, effects };
}
//# sourceMappingURL=world.js.map